import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { CTASection } from '../components/CTASection';
import { motion } from 'framer-motion';
import { ShieldCheck, Users, Target, Award } from 'lucide-react';

const values = [
  {
    icon: ShieldCheck,
    title: 'Reliability',
    description: 'We do what we say. Every load, every time. Our reputation is built on consistent execution.'
  },
  {
    icon: Users,
    title: 'Partnership',
    description: 'We\'re not a faceless broker. We\'re an extension of your supply chain, invested in your success.'
  },
  {
    icon: Target,
    title: 'Transparency',
    description: 'No hidden fees, no surprises. Clear communication from quote to delivery.'
  },
  {
    icon: Award,
    title: 'Excellence',
    description: 'We hold ourselves to high standards and continuously improve our processes.'
  }
];

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-white" data-testid="about-page">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-navy-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-block px-4 py-2 bg-orange-500/10 border border-orange-500/30 rounded-full text-orange-500 font-body text-sm font-medium mb-4"
          >
            About Us
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white uppercase"
          >
            Built on <span className="text-orange-500">Trust</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mt-4 text-base lg:text-lg text-slate-400 font-body max-w-2xl mx-auto"
          >
            Summit Transport & Logistics was founded with a simple mission: provide reliable freight solutions with exceptional service.
          </motion.p>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-navy-900 uppercase mb-6">
                Our Story
              </h2>
              <div className="space-y-4 font-body text-slate-600 leading-relaxed">
                <p>
                  Summit Transport & Logistics was built from frustration with how freight is typically handled. After years working in the freight industry, we saw shippers repeatedly dealing with unreliable brokers, hidden fees, and poor communication.
                </p>
                <p>
                  We built Summit around a simple principle: the best logistics work often goes unnoticed. When freight is handled properly, it moves on time, at the agreed price, and without constant check-ins or surprises. If we&apos;re doing our job right, you don&apos;t have to think about it.
                </p>
                <p>
                  Today, we move freight across the United States for manufacturers, distributors, and businesses of all sizes. Our success isn&apos;t measured only in loads delivered, but in long-term partnerships built on trust, consistency, and accountability.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="rounded-2xl overflow-hidden"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_2decc47a-ac14-4283-b5d1-97b4034d1d29/artifacts/vw9lhlxf_IMG_3309.JPG"
                alt="Summit Transport Team"
                className="w-full h-96 object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 lg:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="font-heading text-3xl sm:text-4xl font-bold text-navy-900 uppercase"
            >
              Our Values
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="mt-4 text-base lg:text-lg text-slate-600 font-body max-w-2xl mx-auto"
            >
              The principles that guide how we do business every day.
            </motion.p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl p-8 border border-slate-200 text-center"
              >
                <div className="w-14 h-14 bg-orange-500/10 rounded-xl flex items-center justify-center mx-auto mb-6">
                  <value.icon className="w-7 h-7 text-orange-500" />
                </div>
                <h3 className="font-heading text-xl font-bold text-navy-900 mb-3">
                  {value.title}
                </h3>
                <p className="font-body text-slate-600 text-sm leading-relaxed">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 lg:py-24 bg-navy-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <span className="font-heading text-5xl font-bold text-orange-500">15+</span>
              <p className="mt-2 font-body text-slate-400">Years Experience</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <span className="font-heading text-5xl font-bold text-orange-500">10K+</span>
              <p className="mt-2 font-body text-slate-400">Loads Moved</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <span className="font-heading text-5xl font-bold text-orange-500">98%</span>
              <p className="mt-2 font-body text-slate-400">On-Time Delivery</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
            >
              <span className="font-heading text-5xl font-bold text-orange-500">5K+</span>
              <p className="mt-2 font-body text-slate-400">Carrier Network</p>
            </motion.div>
          </div>
        </div>
      </section>

      <CTASection />
      <Footer />
    </div>
  );
}
